import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import ForgotPassword from './ForgotPassword';
import { trackAuth, trackError } from '../Utils/analytics';
import '../Auth/Auth.css';

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      trackError('login_validation_failed', 'login');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('https://api.homecookt.com/api/v1/users/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Handle the access_token from your backend response
        const token = data.access_token || data.token || data.authToken || data.accessToken || data.jwt;
        
        if (token) {
          localStorage.setItem('authToken', token);
          localStorage.setItem('userEmail', email);
          
          // Store token type if provided
          if (data.token_type) {
            localStorage.setItem('tokenType', data.token_type);
          }
          
          // Store userid if available (try different possible field names)
          const userid = data.userid || data.user_id || data.id || data.userId;
          if (userid) {
            localStorage.setItem('userid', userid.toString());
          } else {
            // Use email as fallback identifier if no userid provided
            localStorage.setItem('userid', email);
          }
          
          onLogin();
          trackAuth('login');
          
          // Clear the browser history of auth pages and navigate to home
          // This prevents back button from going to login page
          window.history.replaceState(null, '', '/home');
          navigate('/home', { replace: true });
        } else {
          // If no token found, log the response for debugging
          console.log('Login response data:', data);
          setError('Login successful but authentication token not found. Please contact support.');
          trackError('login_token_missing', 'login');
          return;
        }
      } else {
        // Handle different error scenarios with specific messages
        if (response.status === 404) {
          setError('No account found with this email address. Please register first or check your email.');
          trackError('login_user_not_found', 'login');
        } else if (response.status === 401) {
          setError('Incorrect email or password. Please check your credentials and try again.');
          trackError('login_invalid_credentials', 'login');
        } else if (response.status === 403) {
          setError('Account access denied. Please verify your email or contact support.');
          trackError('login_access_denied', 'login');
        } else if (response.status === 400) {
          // Handle validation errors from backend
          if (data.message && data.message.toLowerCase().includes('password')) {
            setError('Invalid password. Please check your password and try again.');
            trackError('login_invalid_password', 'login');
          } else if (data.message && data.message.toLowerCase().includes('email')) {
            setError('Invalid email format. Please enter a valid email address.');
            trackError('login_invalid_email', 'login');
          } else if (data.message && data.message.toLowerCase().includes('user not found')) {
            setError('No account found with this email. Please register first.');
            trackError('login_user_not_found', 'login');
          } else if (data.message && data.message.toLowerCase().includes('invalid credentials')) {
            setError('Invalid email or password. Please check your credentials.');
            trackError('login_invalid_credentials', 'login');
          } else {
            setError(data.message || 'Invalid login details. Please check your information.');
            trackError('login_validation_error', 'login');
          }
        } else if (response.status === 429) {
          setError('Too many login attempts. Please wait a few minutes before trying again.');
          trackError('login_rate_limited', 'login');
        } else if (response.status >= 500) {
          setError('Server error. Please try again later or contact support if the problem persists.');
          trackError('login_server_error', 'login');
        } else {
          // Fallback for any other error codes
          setError(data.message || 'Login failed. Please check your credentials and try again.');
          trackError('login_unknown_error', 'login');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please check your internet connection and try again.');
      trackError('login_network_error', 'login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordClose = () => {
    setShowForgotPassword(false);
  };

  if (showForgotPassword) {
    return (
      <ForgotPassword 
        onClose={handleForgotPasswordClose}
        onBack={() => setShowForgotPassword(false)}
      />
    );
  }

  return (
    <div className="auth-container">
      <h2 className="auth-title">Welcome Back</h2>
      <form className="auth-form" onSubmit={handleLogin}>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={error ? 'error' : ''}
            placeholder="Enter your email address"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <div className="password-input-container">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={error ? 'error' : ''}
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <button 
          type="submit" 
          className="auth-button"
          disabled={isLoading}
        >
          {isLoading ? 'Logging in...' : 'Log In'}
        </button>
      </form>
      
      <div className="forgot-password-link">
        <button
          type="button"
          className="link-button"
          onClick={() => setShowForgotPassword(true)}
        >
          Forgot Password?
        </button>
      </div>
      
      <div className="auth-switch">
        Don't have an account?
        <Link to="/signup">Sign Up</Link>
      </div>
    </div>
  );
};

export default Login;